/**
 * MobileFeed — Lightweight mobile feed component.
 * Replicates FeedClient mobile-mode UI without the heavy desktop overhead.
 * Uses shared hooks for business logic, CSS variables for theming.
 *
 * NO: AnimationLayer, useScale, useDynamicBackground, backdrop-blur, MainNavbar
 */
import React, { useState, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Heart, MessageSquare, Share2, Bookmark, ChevronDown, TrendingUp, TrendingDown,
  Flame, Clock, Calendar, Hash, AlertCircle, Edit3, Repeat2, ThumbsDown,
  MoreVertical, Pin, Trash, Search, Flag,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useFeedData, type FeedPost, type Reply } from '../shared/hooks/useFeedData';
import { useSidebarSearch } from '../shared/hooks/useSidebarSearch';
import { getLegacyPostUrl, getCommunityPostUrl, getForumPostUrl } from '../../utils/postUrls';
import { PostBadges } from '../desktop/PostBadges';
import { canModeratePost } from '../../utils/permissions';
import resolveAvatar from '../../lib/resolveAvatar';
import { MobileSiteThemeCustomizer } from './MobileThemeBottomSheet';
import { SubPanelSwiper } from './Navigation';
import { MobilePostOverlay } from './MobilePostOverlay';
import { ReportModal } from '../shared/ReportModal';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPostUrl(post: FeedPost): string {
  if (post.slug && post.communitySlug && post.postType === 'forum')
    return getForumPostUrl(post.communitySlug, post.slug);
  if (post.slug && post.communitySlug && post.postType === 'community')
    return getCommunityPostUrl(post.communitySlug, post.slug);
  if (post.slug && post.authorUsername)
    return getLegacyPostUrl(post.authorUsername, post.slug);
  return getLegacyPostUrl(post.authorUsername || 'unknown', post.slug || post.id.toString());
}

function selectPreviewComments(replies: Reply[]): Reply[] {
  if (!replies || replies.length === 0) return [];
  if (replies.length <= 3) return replies;
  const selected: Reply[] = [];
  const available = [...replies];
  const mostPopular = available.reduce((prev, curr) => (curr.likes > prev.likes) ? curr : prev);
  selected.push(mostPopular);
  available.splice(available.indexOf(mostPopular), 1);
  const mostControversial = available.reduce((prev, curr) => {
    const prevScore = prev.dislikes > 0 ? prev.dislikes : Math.abs(prev.likes - prev.dislikes);
    const currScore = curr.dislikes > 0 ? curr.dislikes : Math.abs(curr.likes - curr.dislikes);
    return currScore > prevScore ? curr : prev;
  });
  selected.push(mostControversial);
  available.splice(available.indexOf(mostControversial), 1);
  if (available.length > 0) {
    selected.push(available[Math.floor(Math.random() * available.length)]);
  }
  return selected;
}

const TABS = [
  { id: 'all' as const, label: 'All' },
  { id: 'following' as const, label: 'Following' },
  { id: 'friends' as const, label: 'Friends' },
  { id: 'community' as const, label: 'Community' },
  { id: 'tags' as const, label: 'Tags' },
];

const SORT_OPTIONS = [
  { value: 'newest' as const, label: 'Newest', icon: Calendar },
  { value: 'oldest' as const, label: 'Oldest', icon: Clock },
  { value: 'popular' as const, label: 'Popular', icon: TrendingUp },
  { value: 'unpopular' as const, label: 'Unpopular', icon: TrendingDown },
  { value: 'controversial' as const, label: 'Controversial', icon: Flame },
];

// ---------------------------------------------------------------------------
// MobileFeed
// ---------------------------------------------------------------------------

export interface MobileFeedProps {
  currentUser: {
    id: string;
    username: string;
    displayName?: string;
    avatar?: string;
    isSiteAdmin?: boolean;
    isSuperAdmin?: boolean;
    isSiteModerator?: boolean;
  } | null;
  activePanel: number;
  onPanelChange: (index: number) => void;
  screenWidth: number;
}

export function MobileFeed({ currentUser, activePanel, onPanelChange, screenWidth }: MobileFeedProps) {
  const feed = useFeedData();
  const currentSortOption = SORT_OPTIONS.find(o => o.value === feed.sortBy) || SORT_OPTIONS[0];
  const [showPostModal, setShowPostModal] = useState(false);

  const handlePostSubmit = useCallback(async (postData: { title: string; content: string; tags: string[]; mediaUrl: string; mediaPosition: 'above' | 'below' }) => {
    await feed.createPost(postData);
    setShowPostModal(false);
  }, [feed]);

  return (
    <>
      <SubPanelSwiper activePanel={activePanel} onPanelChange={onPanelChange} screenWidth={screenWidth}>
        {[
          <LeftPanel key="left" currentUser={currentUser} onShareClick={() => setShowPostModal(true)} />,
          <CenterPanel key="center" feed={feed} currentUser={currentUser} currentSortOption={currentSortOption} />,
          <RightPanel key="right" currentUser={currentUser} />,
        ]}
      </SubPanelSwiper>

      {/* Post creation overlay — rendered outside SubPanelSwiper via portal */}
      <MobilePostOverlay
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
        onSubmit={handlePostSubmit}
        currentUser={currentUser}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Left Panel — Share button
// ---------------------------------------------------------------------------

function LeftPanel({ currentUser, onShareClick }: { currentUser: MobileFeedProps['currentUser']; onShareClick: () => void }) {
  const [isShareAnimating, setIsShareAnimating] = useState(false);

  return (
    <div className="p-4 flex flex-col items-center gap-3" style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top) + 1rem)' }}>
      {currentUser && (
        <button
          onClick={() => {
            setIsShareAnimating(true);
            setTimeout(() => { onShareClick(); setIsShareAnimating(false); }, 300);
          }}
          className="rounded-lg shadow-lg font-semibold overflow-hidden"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: 'var(--text-color)',
            padding: '16px 48px',
            fontSize: '18px',
          }}
        >
          <span style={{
            display: 'inline-block',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: isShareAnimating ? 0 : 1,
            transform: isShareAnimating ? 'translateX(-20px)' : 'translateX(0)',
          }}>
            Share
          </span>
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Center Panel — Feed with Tabs + Sort + Posts
// ---------------------------------------------------------------------------

function CenterPanel({
  feed,
  currentUser,
  currentSortOption,
}: {
  feed: ReturnType<typeof useFeedData>;
  currentUser: MobileFeedProps['currentUser'];
  currentSortOption: typeof SORT_OPTIONS[number];
}) {
  const navigate = useNavigate();
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showModerateMenu, setShowModerateMenu] = useState<string | null>(null);

  return (
    <div className="flex flex-col items-center w-full px-2 h-full overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* Tab bar */}
      <div className="rounded-lg border mb-3 w-full" style={{
        background: 'var(--tab-bar-bg, rgba(30, 58, 95, 0.95))',
        borderColor: 'rgba(var(--accent-rgb, 6,182,212), 0.25)',
        padding: '0 12px',
      }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => feed.setActiveTab(tab.id)}
                className="font-semibold whitespace-nowrap"
                style={{
                  color: feed.activeTab === tab.id ? 'var(--text-color)' : 'var(--text-color-muted, rgba(255,255,255,0.6))',
                  borderBottom: feed.activeTab === tab.id ? '2px solid var(--color-accent)' : '2px solid transparent',
                  paddingBottom: '4px',
                  paddingTop: '8px',
                  fontSize: '13px',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Sort dropdown */}
          <div className="relative" style={{ zIndex: 41 }}>
            <button
              onClick={() => setShowSortMenu(prev => !prev)}
              className="flex items-center rounded-lg font-medium"
              style={{
                gap: '4px',
                padding: '4px 8px',
                background: 'transparent',
                color: 'var(--text-color)',
                fontSize: '12px',
              }}
            >
              <currentSortOption.icon size={14} />
              <ChevronDown size={14} className={showSortMenu ? 'rotate-180' : ''} style={{ transition: 'transform 0.2s' }} />
            </button>
            {showSortMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowSortMenu(false)} />
                <div className="absolute top-full right-0 rounded-lg overflow-hidden shadow-2xl z-50" style={{
                  background: 'var(--card-bg)',
                  border: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.25)',
                  marginTop: '4px',
                  minWidth: '140px',
                }}>
                  {SORT_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => { feed.setSortBy(option.value); setShowSortMenu(false); }}
                      className="flex items-center gap-2 w-full text-left"
                      style={{
                        padding: '10px 14px',
                        backgroundColor: feed.sortBy === option.value ? 'rgba(var(--accent-rgb, 6,182,212), 0.2)' : 'transparent',
                        color: 'var(--text-color)',
                        fontSize: '13px',
                      }}
                    >
                      <option.icon size={14} style={{ opacity: 0.7 }} />
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Wavy divider */}
      <div className="w-full" style={{ marginTop: '12px', marginBottom: '16px' }}>
        <svg width="100%" height="12" viewBox="0 0 1000 20" preserveAspectRatio="none" style={{ display: 'block' }}>
          <path
            d="M0,10 Q25,0 50,10 T100,10 T150,10 T200,10 T250,10 T300,10 T350,10 T400,10 T450,10 T500,10 T550,10 T600,10 T650,10 T700,10 T750,10 T800,10 T850,10 T900,10 T950,10 T1000,10"
            fill="none"
            stroke="var(--color-accent)"
            strokeWidth="2"
            opacity="0.4"
          />
        </svg>
      </div>

      {/* Loading / Empty */}
      {feed.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin" style={{
            borderColor: 'var(--color-accent)',
            borderTopColor: 'transparent',
          }} />
        </div>
      ) : feed.posts.length === 0 ? (
        <div className="text-center py-16">
          <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-color)' }}>No posts yet</h2>
          <p className="text-sm" style={{ color: 'var(--text-color)', opacity: 0.6 }}>Be the first to share something!</p>
        </div>
      ) : (
        <div className="w-full flex flex-col items-center">
          {feed.posts.map(post => (
            <MobileFeedPostCard
              key={post.id}
              post={post}
              currentUser={currentUser}
              showModerateMenu={showModerateMenu}
              setShowModerateMenu={setShowModerateMenu}
              onLike={feed.likePost}
              onDislike={feed.dislikePost}
              onDelete={feed.deletePost}
              onPin={feed.pinPost}
              onLikeComment={feed.likeComment}
              onDislikeComment={feed.dislikeComment}
              navigate={navigate}
            />
          ))}
        </div>
      )}

      {/* Bottom padding */}
      <div className="h-24 shrink-0" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right Panel — Widgets
// ---------------------------------------------------------------------------

function RightPanel({ currentUser }: { currentUser: MobileFeedProps['currentUser'] }) {
  const navigate = useNavigate();
  const [showThemeCustomizer, setShowThemeCustomizer] = useState(false);
  const search = useSidebarSearch();

  return (
    <div className="pt-6 px-4 flex flex-col gap-4 h-full overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* Theme Customizer */}
      {currentUser && (
        <button
          onClick={() => setShowThemeCustomizer(true)}
          className="rounded-lg font-semibold w-full flex items-center justify-center gap-2"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: 'var(--text-color)',
            padding: '0.75rem 1rem',
            fontSize: '0.875rem',
            minHeight: '48px',
          }}
        >
          Customize Theme
        </button>
      )}

      {/* Navigate Widget */}
      <div className="rounded-lg border" style={{
        background: 'var(--card-bg)',
        borderColor: 'rgba(var(--accent-rgb, 6,182,212), 0.25)',
      }}>
        <h3 className="font-bold text-center border-b py-3" style={{
          color: 'var(--text-color)',
          borderColor: 'rgba(var(--accent-rgb, 6,182,212), 0.25)',
          fontSize: '16px',
        }}>Navigate</h3>
        <div className="p-3">
          <input
            type="text"
            placeholder="Search..."
            value={search.query}
            onChange={e => search.setQuery(e.target.value)}
            onFocus={() => { if (search.query.trim()) search.setShowResults(true); }}
            className="w-full rounded-lg outline-none"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'var(--text-color)',
              border: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.25)',
              padding: '10px 12px',
              fontSize: '14px',
              marginBottom: '10px',
            }}
          />

          {/* Search type tabs */}
          <div className="flex gap-0 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.25)' }}>
            {(['people', 'communities', 'tags'] as const).map(type => (
              <button
                key={type}
                onClick={() => search.setSearchType(type)}
                className="flex-1 font-semibold py-2"
                style={{
                  backgroundColor: search.searchType === type ? 'var(--color-accent)' : 'rgba(255, 255, 255, 0.05)',
                  color: 'var(--text-color)',
                  fontSize: '13px',
                  borderRight: type !== 'tags' ? '1px solid rgba(var(--accent-rgb, 6,182,212), 0.25)' : undefined,
                }}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>

          {/* Search results */}
          {search.showResults && search.query.trim() && (
            <div className="mt-3 rounded-lg border overflow-hidden" style={{
              background: 'var(--card-bg)',
              borderColor: 'rgba(var(--accent-rgb, 6,182,212), 0.25)',
              maxHeight: '300px',
            }}>
              <div className="overflow-y-auto p-2 space-y-1" style={{ maxHeight: '300px' }}>
                {search.isSearching ? (
                  <div className="text-center py-4" style={{ color: 'var(--text-color)', opacity: 0.6 }}>Searching...</div>
                ) : search.searchType === 'people' ? (
                  search.people.length > 0 ? search.people.map(person => (
                    <button
                      key={person.id}
                      onClick={() => { navigate(`/user/${person.username}`); search.clearSearch(); }}
                      className="flex items-center gap-3 p-2 rounded-lg w-full text-left"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                    >
                      {person.avatar ? (
                        <img src={person.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: 'var(--color-accent)', color: 'var(--app-bg)' }}>
                          {person.displayName.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-color)' }}>{person.displayName}</div>
                        <div className="text-xs truncate" style={{ color: 'var(--text-color)', opacity: 0.6 }}>@{person.username}</div>
                      </div>
                    </button>
                  )) : (
                    <div className="text-center py-4 text-sm" style={{ color: 'var(--text-color)', opacity: 0.6 }}>No results</div>
                  )
                ) : search.searchType === 'communities' ? (
                  search.communities.length > 0 ? search.communities.map(comm => (
                    <button
                      key={comm.id}
                      onClick={() => { navigate(`/community/${comm.nameSlug || comm.name.toLowerCase().replace(/\s+/g, '-')}`); search.clearSearch(); }}
                      className="flex items-center gap-3 p-2 rounded-lg w-full text-left"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                    >
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: 'var(--color-accent)', color: 'var(--app-bg)' }}>
                        {comm.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-color)' }}>{comm.name}</div>
                        <div className="text-xs" style={{ color: 'var(--text-color)', opacity: 0.6 }}>{comm.memberCount} members</div>
                      </div>
                    </button>
                  )) : (
                    <div className="text-center py-4 text-sm" style={{ color: 'var(--text-color)', opacity: 0.6 }}>No results</div>
                  )
                ) : (
                  search.tags.length > 0 ? search.tags.map(t => (
                    <button
                      key={t.tag}
                      onClick={() => search.clearSearch()}
                      className="flex items-center gap-2 p-2 rounded-lg w-full text-left"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                    >
                      <Hash size={14} style={{ color: 'var(--color-accent)' }} />
                      <span className="text-sm" style={{ color: 'var(--text-color)' }}>#{t.tag}</span>
                      <span className="text-xs ml-auto" style={{ color: 'var(--text-color)', opacity: 0.5 }}>{t.count} posts</span>
                    </button>
                  )) : (
                    <div className="text-center py-4 text-sm" style={{ color: 'var(--text-color)', opacity: 0.6 }}>No results</div>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="h-24 shrink-0" />

      {/* Theme Customizer */}
      {showThemeCustomizer && currentUser && (
        <MobileSiteThemeCustomizer
          isOpen={showThemeCustomizer}
          onClose={() => setShowThemeCustomizer(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Post Card — matches FeedClient's post card rendering
// ---------------------------------------------------------------------------

function MobileFeedPostCard({
  post,
  currentUser,
  showModerateMenu,
  setShowModerateMenu,
  onLike,
  onDislike,
  onDelete,
  onPin,
  onLikeComment,
  onDislikeComment,
  navigate,
}: {
  post: FeedPost;
  currentUser: MobileFeedProps['currentUser'];
  showModerateMenu: string | null;
  setShowModerateMenu: (id: string | null) => void;
  onLike: (id: string) => void;
  onDislike: (id: string) => void;
  onDelete: (id: string) => void;
  onPin: (id: string, isPinned: boolean) => void;
  onLikeComment: (commentId: string, postId: string) => void;
  onDislikeComment: (commentId: string, postId: string) => void;
  navigate: (path: string) => void;
}) {
  const [commentMenuId, setCommentMenuId] = useState<string | null>(null);
  const [reportingCommentId, setReportingCommentId] = useState<string | null>(null);

  const canModerate = currentUser && canModeratePost(post as any, {
    id: parseInt(currentUser.id),
    username: currentUser.username,
    isAdmin: currentUser.isSiteAdmin || currentUser.isSuperAdmin || false,
    isModerator: currentUser.isSiteModerator || false,
  });

  return (
    <div
      className="rounded-lg p-4 border relative w-full"
      style={{
        background: 'var(--card-bg)',
        borderColor: 'rgba(var(--accent-rgb, 6,182,212), 0.25)',
        marginBottom: '1rem',
      }}
    >
      {/* Post Badges */}
      <PostBadges post={post as any} textColor="var(--text-color)" accentColor="var(--color-accent)" />

      {/* Moderation */}
      {canModerate && (
        <div className="absolute top-3 right-3" style={{ zIndex: 10 }}>
          <button
            onClick={() => setShowModerateMenu(showModerateMenu === post.id ? null : post.id)}
            className="p-1.5 rounded-lg"
            style={{ backgroundColor: 'rgba(var(--accent-rgb, 6,182,212), 0.12)', color: 'var(--text-color)' }}
          >
            <MoreVertical size={16} />
          </button>
          {showModerateMenu === post.id && (
            <>
              <div className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl min-w-[140px]" style={{
                backgroundColor: 'var(--card-bg)',
                border: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.4)',
                zIndex: 1001,
              }}>
                <button
                  onClick={() => { onPin(post.id, !!post.isPinned); setShowModerateMenu(null); }}
                  className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium"
                  style={{ color: 'var(--text-color)' }}
                >
                  <Pin size={16} />
                  {post.isPinned ? 'Unpin' : 'Pin'}
                </button>
                <button
                  onClick={() => { if (confirm('Delete this post?')) { onDelete(post.id); setShowModerateMenu(null); } }}
                  className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium"
                  style={{ color: '#ef4444' }}
                >
                  <Trash size={16} />
                  Delete
                </button>
              </div>
              <div className="fixed inset-0 z-40" onClick={() => setShowModerateMenu(null)} />
            </>
          )}
        </div>
      )}

      {/* Author row */}
      <div className="flex items-start gap-3 mb-3">
        <Link to={post.authorUsername ? `/user/${post.authorUsername}` : '#'} className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{
          background: `linear-gradient(to bottom right, var(--color-accent), var(--color-accent))`,
        }}>
          {post.authorAvatar ? (
            <img src={post.authorAvatar} alt={post.author} className="w-full h-full rounded-full object-cover" />
          ) : (
            <span className="text-sm font-bold" style={{ color: 'var(--app-bg)' }}>{post.author.charAt(0).toUpperCase()}</span>
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col gap-0.5 mb-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold truncate" style={{ color: 'var(--text-color)' }}>{post.author}</h3>
              {post.communityName && (
                <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-color)', opacity: 0.6 }}>in {post.communityName}</span>
              )}
              <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-color)', opacity: 0.6 }}>{post.timestamp}</span>
            </div>
            {post.authorUsername && (
              <span className="text-xs" style={{ color: 'var(--text-color)', opacity: 0.6 }}>@{post.authorUsername}</span>
            )}
          </div>
        </div>
      </div>

      {/* Moderation disclaimer */}
      {post.editedByModeratorId && (
        <div className="mb-3 px-3 py-2 rounded border" style={{ backgroundColor: '#854d0e20', borderColor: '#ca8a0480' }}>
          <div className="flex items-center gap-2">
            <AlertCircle size={14} className="text-yellow-400" />
            <p className="text-xs text-yellow-100">This post was edited by a moderator</p>
          </div>
          {post.moderatorEditReason && (
            <p className="text-xs text-yellow-100/80 mt-1">Reason: {post.moderatorEditReason}</p>
          )}
        </div>
      )}

      {/* Title + Content */}
      <Link to={getPostUrl(post)} className="block">
        {post.title && (
          <h2 className="text-base font-bold mb-2" style={{ color: 'var(--text-color)' }}>{post.title}</h2>
        )}
        {post.aboveContent && post.mediaUrl && (
          <div className="mb-3 rounded-lg overflow-hidden">
            <img src={post.mediaUrl} alt="" className="w-full h-auto" style={{ maxHeight: '300px', objectFit: 'cover' }} loading="lazy" />
          </div>
        )}
        <p className="text-sm leading-relaxed mb-3 whitespace-pre-wrap" style={{ color: 'var(--text-color)' }}>
          {post.content}
        </p>
        {post.belowContent && post.mediaUrl && (
          <div className="mt-3 rounded-lg overflow-hidden">
            <img src={post.mediaUrl} alt="" className="w-full h-auto" style={{ maxHeight: '300px', objectFit: 'cover' }} loading="lazy" />
          </div>
        )}
      </Link>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {post.tags.map((tag, idx) => (
            <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs" style={{
              backgroundColor: 'rgba(var(--accent-rgb, 6,182,212), 0.2)',
              color: 'var(--text-color)',
            }}>
              <Hash size={10} />
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => onLike(post.id)} className="flex items-center gap-1.5" style={{ color: post.liked ? '#f87171' : 'var(--text-color)', opacity: post.liked ? 1 : 0.6, minHeight: '44px' }}>
            <Heart size={16} className={post.liked ? 'fill-current' : ''} />
            <span className="text-xs">{post.likes}</span>
          </button>
          <button onClick={() => onDislike(post.id)} className="flex items-center gap-1.5" style={{ color: post.disliked ? '#f97316' : 'var(--text-color)', opacity: post.disliked ? 1 : 0.6, minHeight: '44px' }}>
            <ThumbsDown size={16} className={post.disliked ? 'fill-current' : ''} />
            <span className="text-xs">{post.dislikes}</span>
          </button>
          <button onClick={() => navigate(getPostUrl(post))} className="flex items-center gap-1.5" style={{ color: 'var(--text-color)', opacity: 0.6, minHeight: '44px' }}>
            <MessageSquare size={16} />
            <span className="text-xs">{post.comments}</span>
          </button>
          <button className="flex items-center gap-1.5" style={{ color: 'var(--text-color)', opacity: 0.6, minHeight: '44px' }}>
            <Share2 size={16} />
          </button>
        </div>
        <button style={{ color: 'var(--text-color)', opacity: 0.6 }}>
          <Bookmark size={16} />
        </button>
      </div>

      {/* Comment preview */}
      {post.replies && post.replies.length > 0 && (() => {
        const previewComments = selectPreviewComments(post.replies);
        return (
          <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.12)' }}>
            <div className="space-y-3">
              {previewComments.map(reply => (
                <div key={reply.id} className="flex gap-3">
                  <Link
                    to={`/user/${reply.author.username}`}
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'var(--color-accent)' }}
                  >
                    {reply.author.profilePicture ? (
                      <img src={resolveAvatar(reply.author.profilePicture)} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="text-xs font-bold" style={{ color: 'var(--app-bg)' }}>
                        {(reply.author.profileName || reply.author.username).charAt(0).toUpperCase()}
                      </span>
                    )}
                  </Link>
                  <div className="flex-1 relative">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold" style={{ color: 'var(--text-color)' }}>
                        {reply.author.profileName || reply.author.username}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--text-color)', opacity: 0.4 }}>
                        {new Date(reply.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      <div className="ml-auto relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCommentMenuId(commentMenuId === reply.id ? null : reply.id);
                          }}
                          className="p-0.5 rounded"
                          style={{ color: 'var(--text-color)', opacity: 0.3 }}
                        >
                          <MoreVertical size={14} />
                        </button>
                        {commentMenuId === reply.id && (
                          <div
                            className="absolute right-0 top-6 z-50 rounded-lg py-1 shadow-lg"
                            style={{
                              background: 'var(--navbar-bg, #1a1a2e)',
                              border: '1px solid rgba(255,255,255,0.12)',
                              minWidth: 120,
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setCommentMenuId(null);
                                setReportingCommentId(reply.id);
                              }}
                              className="flex items-center gap-2 w-full px-3 py-2 text-xs"
                              style={{ color: '#ef4444' }}
                            >
                              <Flag size={12} />
                              Report
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--text-color)', opacity: 0.8 }}>{reply.content}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <button
                        onClick={() => onLikeComment(reply.id, post.id)}
                        className="flex items-center gap-1"
                        style={{ color: reply.liked ? '#ef4444' : 'var(--text-color)', opacity: reply.liked ? 1 : 0.4 }}
                      >
                        <Heart size={12} fill={reply.liked ? '#ef4444' : 'none'} />
                        <span className="text-xs">{reply.likes}</span>
                      </button>
                      <button
                        onClick={() => onDislikeComment(reply.id, post.id)}
                        className="flex items-center gap-1"
                        style={{ color: reply.disliked ? '#f97316' : 'var(--text-color)', opacity: reply.disliked ? 1 : 0.4 }}
                      >
                        <ThumbsDown size={12} fill={reply.disliked ? '#f97316' : 'none'} />
                        <span className="text-xs">{reply.dislikes}</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {post.comments > 3 && (
              <div className="mt-2">
                <button
                  className="text-xs"
                  style={{ color: 'var(--color-accent)' }}
                  onClick={() => navigate(getPostUrl(post))}
                >
                  View all {post.comments} comments
                </button>
              </div>
            )}
          </div>
        );
      })()}
      <ReportModal
        isOpen={!!reportingCommentId}
        onClose={() => setReportingCommentId(null)}
        commentId={reportingCommentId || undefined}
      />
    </div>
  );
}
